import { Img } from "@/components/site/Img";
import { useState } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { birdBaths, fallbackPrice } from "@/components/site/availableCatalog";
import { ReserveModal } from "@/components/site/ReserveModal";
import { track } from "@/lib/track";

const BirdBathCard = ({ piece }) => {
  const [showReserve, setShowReserve] = useState(false);
  const priced = piece.price && piece.price !== "Ask for pricing";
  return (
    <article
      data-testid={`birdbath-${piece.testid}`}
      className="card-lift bg-white border border-[#D5CEBD] flex flex-col overflow-hidden"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[#EFEBE1]">
        <Img
          src={piece.image}
          alt={piece.alt || piece.title}
          loading="lazy"
          className="w-full h-full object-cover object-top"
        />
        <span data-testid={`birdbath-${piece.testid}-badge`} className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 bg-[#2F5D3A] text-white text-[9px] tracking-[0.22em] uppercase">
          <span className="w-1.5 h-1.5 rounded-full bg-[#9BE39F]" />
          Check availability
        </span>
      </div>
      <div className="p-5 md:p-6 flex flex-col flex-1">
        <h3 className="font-serif text-lg md:text-xl text-[#2C2A28] leading-snug">{piece.title}</h3>
        <div
          data-testid={`birdbath-${piece.testid}-price`}
          className={priced ? "text-[#7A6547] font-medium text-lg mt-1 mb-2" : "text-[#8A867F] text-[10px] tracking-[0.12em] uppercase mt-1.5 mb-2"}
        >
          {piece.price || fallbackPrice(piece)}
        </div>
        {piece.dims && (
          <div className="text-[#5C5955] text-xs mb-1">Size: {piece.dims}</div>
        )}
        {piece.weight && (
          <div className="text-[#5C5955] text-xs mb-1">Weight: {piece.weight}</div>
        )}
        <div className="text-[#5C5955] text-xs leading-relaxed mb-4">
          Custom colors &amp; finishes available
        </div>
        <div className="mt-auto">
          <button
            type="button"
            onClick={() => {
              setShowReserve(true);
              track("reserve_open", piece.title);
            }}
            data-testid={`birdbath-${piece.testid}-reserve`}
            className="w-full py-3 bg-[#1F1E1C] text-[#F9F6F0] text-[10px] tracking-[0.22em] uppercase hover:bg-[#7A6547] transition-colors"
          >
            Inquire About This Piece
          </button>
        </div>
      </div>
      <ReserveModal
        open={showReserve}
        onClose={() => setShowReserve(false)}
        piece={piece.title}
        mode="availability"
      />
    </article>
  );
};

export default function BestSellingBirdBaths({ onBrowseAll }) {
  return (
    <section
      id="birdbaths"
      data-testid="birdbaths-section"
      className="bg-[#EFEBE1] py-10 md:py-12 border-t border-[#D5CEBD]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8 md:mb-10">
          <div>
            <div className="section-eyebrow mb-4">
              <span>Best Sellers</span>
            </div>
            <h2
              data-testid="birdbaths-heading"
              className="font-serif font-light text-[#2C2A28] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            >
              Our best-selling birdbaths.
            </h2>
          </div>
          <p className="text-[#5C5955] max-w-md text-sm md:text-base leading-relaxed">
            Choose a style you love, then make it your own with a custom
            finish. Available pieces change often, so contact Chris to check
            availability on your favorite.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {birdBaths.map((b) => (
            <BirdBathCard key={b.testid} piece={b} />
          ))}
        </div>


        <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p data-testid="birdbaths-inventory-note" className="text-[#6F6A60] text-xs leading-relaxed max-w-md">
            {onBrowseAll
              ? "Chris keeps casting these, so a style shown here can usually be made again in the finish you want."
              : "Inventory moves faster than the website. A bird bath shown here may already be spoken for, so inquire and Chris will confirm, or cast you another in the finish you want."}
          </p>
          {onBrowseAll ? (
            <button
              type="button"
              onClick={onBrowseAll}
              data-testid="birdbaths-browse-all"
              className="inline-flex items-center gap-2 text-[#7A6547] hover:text-[#5E4D35] text-xs tracking-[0.22em] uppercase border-b border-[#7A6547]/40 hover:border-[#7A6547] pb-1 transition-colors whitespace-nowrap"
            >
              Browse All Pieces
              <ArrowRight size={13} strokeWidth={1.5} />
            </button>
          ) : (
            <Link
              to="/available-now"
              data-testid="birdbaths-see-all"
              className="inline-flex items-center gap-2 text-[#7A6547] hover:text-[#5E4D35] text-xs tracking-[0.22em] uppercase border-b border-[#7A6547]/40 hover:border-[#7A6547] pb-1 transition-colors whitespace-nowrap"
            >
              See All Birdbaths &amp; Pieces
              <ArrowRight size={13} strokeWidth={1.5} />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
